import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useCreateSubscriptionMutation } from '../../../features/marketplace/marketplaceApi'
import { useLazyGetConnectionInstanceQuery } from '../../../features/connections/connectionsApi'
import { paths } from '../../../app/routes'

const requiresAuthentication = (connection) => Boolean(connection) && Boolean(
    (connection.source && connection.source.isAuthenticationRequired) ||
    (connection.target && connection.target.isAuthenticationRequired)
)

const toAnswers = (answers) => Object.keys(answers)
    .filter((questionId) => String(answers[questionId]).trim() !== '')
    .map((questionId) => ({ questionId: parseInt(questionId, 10), answer: String(answers[questionId]).trim() }))

const useCreateConnector = (integration, plan) => {
    const history = useHistory()
    const [createSubscription] = useCreateSubscriptionMutation()
    const [getConnectionInstance] = useLazyGetConnectionInstanceQuery()
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState(null)

    const create = async (answers = {}) => {
        setError(null)
        setIsCreating(true)
        try {
            const subscriptionAnswers = toAnswers(answers)
            const subscription = await createSubscription({
                connectorVersionId: parseInt(plan.productVersionId, 10),
                connectorName: integration.name,
                sourceVersion: String(plan.sourceVersion),
                targetVersion: String(plan.targetVersion),
                ...(subscriptionAnswers.length > 0 ? { subscriptionAnswers } : {})
            }).unwrap()
            if (!subscription || subscription.result === false) {
                setError((subscription && subscription.message) || 'The connector could not be created.')
                setIsCreating(false)
                return
            }
            const ids = { connectorId: subscription.connectorId, instanceId: subscription.instanceId }
            const connection = await getConnectionInstance(ids).unwrap().catch(() => null)
            history.replace(requiresAuthentication(connection)
                ? paths.authentication(ids.connectorId, ids.instanceId)
                : paths.setup(ids.connectorId, ids.instanceId))
        } catch (requestError) {
            setError('The connector could not be created. Please try again.')
            setIsCreating(false)
        }
    }

    return { create, isCreating, error }
}

export default useCreateConnector
